import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import type {
  PaymentsOverview,
  PaymentDetails,
  NotificationsOverview,
  NotificationDetails,
  AuditLogs,
  ApplicantGroups,
  Balances,
  Transactions,
  MyReviews,
  ProgramHome,
} from '@aidkitorg/types/src/survey';
import InterfaceContext from '../../../Context';
import { usePost } from '../../../API';

// Union type of all page config types
type AdminPanelPageConfig =
  | PaymentsOverview
  | PaymentDetails
  | NotificationsOverview
  | NotificationDetails
  | AuditLogs
  | ApplicantGroups
  | Balances
  | Transactions
  | MyReviews
  | ProgramHome;

// Type for the API response
interface PageConfigResponse {
  data: {
    config: AdminPanelPageConfig;
  };
}

// Display states
type DisplayState = 'visible' | 'hidden';
type FilterableDisplayState = 'visibleAndFilterable' | DisplayState;

interface PageConfigOptions {
  kind: string;
  enabled?: boolean;
}

/**
 * Custom hook for fetching and working with Admin Panel page configurations
 *
 * @example
 * ```tsx
 * const {
 *   pageConfig,
 *   isLoading,
 *   getLocalizedText,
 *   isVisible,
 *   isFilterable
 * } = useAdminPanelPageConfig('Payments Overview');
 *
 * const pageTitle = getLocalizedText(['pageHeader', 'title'], 'Payments');
 * const showMetric = isVisible(['summaryMetrics', 'enableTotalPayments']);
 * const isColumnFilterable = isFilterable(['columns', 'paymentStatus']);
 * ```
 */
export function useAdminPanelPageConfig<T extends AdminPanelPageConfig = AdminPanelPageConfig>(
  options: PageConfigOptions | string
) {
  const context = useContext(InterfaceContext);
  const fetchPageConfig = usePost('/api/admin_panel/page_config');

  // Allow passing just a string for convenience
  const kind = typeof options === 'string' ? options : options.kind;
  const enabled = typeof options === 'string' ? true : options.enabled ?? true;

  const query = useQuery<PageConfigResponse>({
    queryKey: ['adminPanelPageConfig', kind],
    queryFn: async () => {
      const response = await fetchPageConfig({ kind });
      return response as PageConfigResponse;
    },
    enabled,
  });

  const pageConfig = query.data?.data?.config as T | undefined;

  /**
   * Helper to get localized text from a nested path in the config
   * Automatically handles Text type objects with language keys
   *
   * @param path - Array of keys to traverse in the config object
   * @param fallback - Fallback text if path doesn't exist or has no translation
   * @returns Localized text string
   */
  const getLocalizedText = (path: string[], fallback: string = ''): string => {
    let value: any = pageConfig;
    for (const key of path) {
      value = value?.[key];
      if (!value) return fallback;
    }

    // Handle Text type objects
    if (typeof value === 'object' && value !== null) {
      return value[context.lang] || value['en'] || fallback;
    }

    return value || fallback;
  };

  /**
   * Helper to get rich text from a nested path in the config
   * Automatically handles RichText type objects with language keys
   *
   * @param path - Array of keys to traverse in the config object
   * @param fallback - Fallback text if path doesn't exist or has no translation
   * @returns Localized rich text string
   */
  const getLocalizedRichText = (path: string[], fallback: string = ''): string => {
    // RichText follows the same structure as Text
    return getLocalizedText(path, fallback);
  };

  /**
   * Check if a component/field is visible (DisplayState = 'visible')
   * Works for both DisplayState and FilterableDisplayState
   *
   * @param path - Array of keys to traverse in the config object
   * @returns true if the value is 'visible' or 'visibleAndFilterable'
   */
  const isVisible = (path: string[]): boolean => {
    let value: any = pageConfig;
    for (const key of path) {
      value = value?.[key];
      if (value === undefined || value === null) return false;
    }
    return value === 'visible' || value === 'visibleAndFilterable';
  };

  /**
   * Check if a component/field is filterable (FilterableDisplayState = 'visibleAndFilterable')
   *
   * @param path - Array of keys to traverse in the config object
   * @returns true if the value is 'visibleAndFilterable'
   */
  const isFilterable = (path: string[]): boolean => {
    let value: any = pageConfig;
    for (const key of path) {
      value = value?.[key];
      if (value === undefined || value === null) return false;
    }
    return value === 'visibleAndFilterable';
  };

  /**
   * Get the display state value directly
   * Useful when you need the actual state value for more complex logic
   *
   * @param path - Array of keys to traverse in the config object
   * @returns The DisplayState or FilterableDisplayState value
   */
  const getDisplayState = (path: string[]): DisplayState | FilterableDisplayState | undefined => {
    let value: any = pageConfig;
    for (const key of path) {
      value = value?.[key];
      if (value === undefined || value === null) return undefined;
    }
    return value as DisplayState | FilterableDisplayState;
  };

  return {
    // Query state
    ...query,

    // Typed config
    pageConfig,

    // Helper functions
    getLocalizedText,
    getLocalizedRichText,
    isVisible,
    isFilterable,
    getDisplayState,
  };
}

/**
 * Type-safe wrapper for specific page configs
 *
 * @example
 * ```tsx
 * const config = usePaymentsOverviewConfig();
 * // config.pageConfig is typed as PaymentsOverview | undefined
 * ```
 */
export const usePaymentsOverviewConfig = () =>
  useAdminPanelPageConfig<PaymentsOverview>('Payments Overview');

export const usePaymentDetailsConfig = () =>
  useAdminPanelPageConfig<PaymentDetails>('Payment Details');

export const useNotificationsOverviewConfig = () =>
  useAdminPanelPageConfig<NotificationsOverview>('Notifications Overview');

export const useNotificationDetailsConfig = () =>
  useAdminPanelPageConfig<NotificationDetails>('Notification Details');

export const useAuditLogsConfig = () =>
  useAdminPanelPageConfig<AuditLogs>('Audit Logs');

export const useApplicantGroupsConfig = () =>
  useAdminPanelPageConfig<ApplicantGroups>('Applicant Groups');

export const useBalancesConfig = () =>
  useAdminPanelPageConfig<Balances>('Balances');

export const useTransactionsConfig = () =>
  useAdminPanelPageConfig<Transactions>('Transactions');

export const useMyReviewsConfig = () =>
  useAdminPanelPageConfig<MyReviews>('My Reviews');

export const useProgramHomeConfig = () =>
  useAdminPanelPageConfig<ProgramHome>('Program Home');
